import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { scheduledClosingsApi } from '../../../api/adminApi';
import {
  scheduledClosingId,
  scheduledClosingDateInputValue,
  formatScheduledClosingDateShort,
  todayLocalISODate,
} from './scheduledClosingHelpers';
import '../Events/EventsList.css';

const DEFAULT_LIMIT = 100;
const DELETE_ALL_PHRASE = 'DELETE ALL SCHEDULED CLOSINGS';

function ScheduledClosingsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formClosingDate, setFormClosingDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllPhrase, setDeleteAllPhrase] = useState('');
  const [deleteAllBusy, setDeleteAllBusy] = useState(false);

  const loadList = (pageNum = page) => {
    setLoading(true);
    setError('');
    scheduledClosingsApi
      .list(pageNum, limit)
      .then((res) => {
        setItems(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load scheduled closings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingItem(null);
    setFormTitle('');
    setFormClosingDate(todayLocalISODate());
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (row) => {
    const sid = scheduledClosingId(row);
    if (!sid) return;
    setEditingItem(row);
    setFormTitle(row.title ?? '');
    setFormClosingDate(scheduledClosingDateInputValue(row) || todayLocalISODate());
    setFormError('');
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingItem(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!formClosingDate || !/^\d{4}-\d{2}-\d{2}$/.test(formClosingDate)) {
      setFormError('Closing date is required (use a valid date).');
      return;
    }

    const payload = {
      title: formTitle.trim(),
      closing_date: formClosingDate,
    };

    if (editingItem) {
      const sid = scheduledClosingId(editingItem);
      if (!sid) return;
      const nextTitle = formTitle.trim();
      const nextDate = formClosingDate;
      const prevTitle = (editingItem.title ?? '').trim();
      const prevDate = scheduledClosingDateInputValue(editingItem);
      if (nextTitle === prevTitle && nextDate === prevDate) {
        setFormError('Change the title or closing date before saving.');
        return;
      }
      setFormSaving(true);
      scheduledClosingsApi
        .update(sid, payload)
        .then(() => {
          closeFormModal();
          loadList(page);
        })
        .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
        .finally(() => setFormSaving(false));
      return;
    }

    setFormSaving(true);
    scheduledClosingsApi
      .create(payload)
      .then(() => {
        closeFormModal();
        loadList(page);
      })
      .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
      .finally(() => setFormSaving(false));
  };

  const handleDeleteClick = (row) => setDeleteTarget(row);
  const handleDeleteCancel = () => setDeleteTarget(null);
  const handleDeleteConfirm = () => {
    const id = scheduledClosingId(deleteTarget);
    if (!id) return;
    setDeleting(true);
    scheduledClosingsApi
      .delete(id)
      .then(() => {
        setDeleteTarget(null);
        loadList(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const openDeleteAll = () => {
    setDeleteAllPhrase('');
    setDeleteAllOpen(true);
  };

  const closeDeleteAll = () => {
    if (deleteAllBusy) return;
    setDeleteAllOpen(false);
    setDeleteAllPhrase('');
  };

  const handleDeleteAllConfirm = () => {
    if (deleteAllPhrase.trim() !== DELETE_ALL_PHRASE) return;
    setDeleteAllBusy(true);
    setError('');
    scheduledClosingsApi
      .deleteAll()
      .then(() => {
        closeDeleteAll();
        loadList(1);
      })
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete all scheduled closings');
        closeDeleteAll();
      })
      .finally(() => setDeleteAllBusy(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isEditing = editingItem != null;

  return (
    <div className="admin-events">
      <div className="admin-events-header">
        <h2>Scheduled closings</h2>
        <div className="admin-events-header-actions">
          <button
            type="button"
            className="admin-events-delete-all"
            onClick={openDeleteAll}
            disabled={loading || items.length === 0}
          >
            Delete all
          </button>
          <button type="button" className="admin-events-add" onClick={openAddModal}>
            Add closing
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-events-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-events-loading">Loading scheduled closings…</div>
      ) : (
        <div className="admin-events-table-wrap">
          <table className="admin-events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Closing date</th>
                <th>Created</th>
                <th className="admin-events-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="admin-events-empty">
                    No scheduled closings yet.{' '}
                    <button type="button" className="admin-events-empty-btn" onClick={openAddModal}>
                      Add one
                    </button>
                    .
                  </td>
                </tr>
              ) : (
                items.map((row, rowIdx) => {
                  const sid = scheduledClosingId(row);
                  return (
                    <tr key={sid || `sc-row-${rowIdx}`}>
                      <td className="admin-events-title-cell">{row.title || '—'}</td>
                      <td className="admin-events-date-cell">
                        {formatScheduledClosingDateShort(scheduledClosingDateInputValue(row))}
                      </td>
                      <td className="admin-events-date-cell">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="admin-events-actions-col">
                        {sid ? (
                          <Link
                            to={`/admin/scheduled-closings/${sid}`}
                            className="admin-events-btn admin-events-btn-view"
                          >
                            View
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          className="admin-events-btn admin-events-btn-edit"
                          onClick={() => openEditModal(row)}
                          disabled={!sid}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-events-btn admin-events-btn-delete"
                          onClick={() => handleDeleteClick(row)}
                          disabled={!sid}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-events-pagination">
          <button
            type="button"
            className="admin-events-page-btn"
            onClick={() => loadList(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-events-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-events-page-btn"
            onClick={() => loadList(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onRequestClose={() => {
          if (!formSaving) closeFormModal();
        }}
        contentLabel={isEditing ? 'Edit scheduled closing' : 'Add scheduled closing'}
        className="admin-events-modal-content"
        overlayClassName="admin-events-modal-overlay"
      >
        <div className="admin-events-modal-inner">
          <h3>{isEditing ? 'Edit scheduled closing' : 'Add scheduled closing'}</h3>
          {isEditing && (
            <p className="admin-events-modal-lead">Update the title or closing date, then save.</p>
          )}
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-events-form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="admin-events-form-field">
              <label htmlFor="sc-form-title">Title *</label>
              <input
                id="sc-form-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Memorial Day"
                disabled={formSaving}
              />
            </div>

            <div className="admin-events-form-field">
              <label htmlFor="sc-form-date">Closing date *</label>
              <input
                id="sc-form-date"
                type="date"
                value={formClosingDate}
                onChange={(e) => setFormClosingDate(e.target.value)}
                disabled={formSaving}
              />
            </div>

            <div className="admin-events-form-actions">
              <button
                type="button"
                className="admin-events-form-cancel"
                onClick={closeFormModal}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button type="submit" className="admin-events-form-submit" disabled={formSaving}>
                {formSaving ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save changes' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteTarget && (
        <div className="admin-events-delete-modal" role="dialog" aria-modal="true" aria-labelledby="sc-del-title">
          <div className="admin-events-delete-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-events-delete-box">
            <h3 id="sc-del-title">Delete scheduled closing?</h3>
            <p>
              This soft-deletes &ldquo;{deleteTarget.title || 'this closing'}&rdquo;. This cannot be undone from the
              admin UI.
            </p>
            <div className="admin-events-delete-actions">
              <button
                type="button"
                className="admin-events-btn admin-events-btn-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-events-btn admin-events-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAllOpen && (
        <div className="admin-events-delete-modal" role="dialog" aria-modal="true" aria-labelledby="sc-del-all-title">
          <div className="admin-events-delete-backdrop" onClick={closeDeleteAll} aria-hidden="true" />
          <div className="admin-events-delete-box admin-events-delete-box--wide">
            <h3 id="sc-del-all-title">Delete all scheduled closings?</h3>
            <p>
              This calls <code className="admin-events-code">DELETE /admin/scheduled-closings</code> and soft-deletes
              every scheduled closing. Type the phrase below exactly to confirm.
            </p>
            <label className="admin-events-delete-all-label" htmlFor="sc-delete-all-phrase">
              Type: <strong>{DELETE_ALL_PHRASE}</strong>
            </label>
            <input
              id="sc-delete-all-phrase"
              type="text"
              className="admin-events-delete-all-input"
              value={deleteAllPhrase}
              onChange={(e) => setDeleteAllPhrase(e.target.value)}
              autoComplete="off"
              disabled={deleteAllBusy}
            />
            <div className="admin-events-delete-actions">
              <button
                type="button"
                className="admin-events-btn admin-events-btn-cancel"
                onClick={closeDeleteAll}
                disabled={deleteAllBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-events-btn admin-events-btn-delete"
                onClick={handleDeleteAllConfirm}
                disabled={deleteAllBusy || deleteAllPhrase.trim() !== DELETE_ALL_PHRASE}
              >
                {deleteAllBusy ? 'Deleting…' : 'Delete all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduledClosingsList;
